import React, { useEffect } from "react";
import { Redirect } from "next";
import { parse } from "qs";

import getProductsV3 from "@/api/requests/consumerApi/getProductsV3";
import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import getShopCitiesNearMe from "@/api/requests/getShopCitiesNearMe";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import serverConfig from "@/config/server";
import { City } from "@/custom-types/CitiesNearMe";
import { Query } from "@/custom-types/Query";
import useSyncUrlWithLocation from "@/hooks/useSyncUrlWithLocation";
import { useDispatch } from "@/redux/hooks";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import {
  getLocationParamRedirect,
  syncCookieLocationWithLocationParam,
} from "@/utils/getServerSidePropsLocationParamUtils";
import logError from "@/utils/logError";
import {
  LEGACY_FILTER_MAP,
  normalizeShopFilterParam,
} from "@/utils/normalizeShopFilterParam";
import { leaflyShopStringify } from "@/utils/qs";
import { getCountryCodeHeader } from "@/utils/requestContext";
import { getSplitKey } from "@/utils/split/getSplitKey";

import { ShopItem } from "@/components/ShopItemCardWithATB/types";
import { AvailableFilter } from "@/components/UnifiedShop/DynamicFilters/types";
import UnifiedShop, {
  UNIFIED_SHOP_PAGE_SIZE,
} from "@/components/UnifiedShop/UnifiedShop";

export type ShopProps = {
  shopItems: ShopItem[];
  availableFilters: AvailableFilter[];
  citiesNearby: City[];
  totalCount: number;
  complianceRuleset: ComplianceRuleset | null;
  debugAPIQueryString?: string;
};

const ShopPage: React.FC<ShopProps> = ({
  complianceRuleset,
  debugAPIQueryString,
  ...newProps
}) => {
  const dispatch = useDispatch();

  useSyncUrlWithLocation();

  useEffect(() => {
    if (debugAPIQueryString) {
      getSplitKey().then((splitKey) => {
        /* eslint-disable no-console -- Useful for debugging /shop's backend API */
        console.log("Leafly internal dev logging:");
        console.log(`User Split Key: ${splitKey}`);
        console.log(`Products V3 API QS: ${debugAPIQueryString}`);
        /* eslint-enable no-console */
      });
    }
  }, [debugAPIQueryString]);

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  return <UnifiedShop {...newProps} />;
};

export const getServerSideProps = createGetServerSideProps<ShopProps>(
  async (context) => {
    const normalizedFiltersRedirect = getLegacyFilterParamsRedirect(context);
    if (normalizedFiltersRedirect) {
      return { redirect: normalizedFiltersRedirect };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const userLocation = await getUserLocationData(countryCode, context);

    const locationParamRedirect = getLocationParamRedirect(
      context,
      userLocation,
    );
    if (locationParamRedirect) {
      return { redirect: locationParamRedirect };
    }

    await syncCookieLocationWithLocationParam(context, userLocation);

    try {
      // * userLocation needs to be refreshed after syncCookieLocationWithLocationParam
      const userLocation = await getUserLocationData(countryCode, context);
      const [url, _hash] = context.resolvedUrl.split("#");
      const [_, query] = url.split("?");
      const { filter, fulfillment, q, sort, page } = parse(query);
      const currentPage = (parseInt(page as string) || 1) - 1;
      const { coordinates, state_code } = userLocation || {};

      const productHeaders = new Headers();
      if (context.req.headers["cookie"]) {
        productHeaders.append("cookie", context.req.headers["cookie"]);
      }

      const apiParams = {
        filter: filter as Query,
        fulfillment: fulfillment as string[],
        headers: productHeaders,
        lat: coordinates?.latitude || coordinates?.lat,
        lon: coordinates?.longitude || coordinates?.lon,
        q: q as string,
        skip: currentPage * UNIFIED_SHOP_PAGE_SIZE,
        sort: sort as Query,
        take: UNIFIED_SHOP_PAGE_SIZE,
      };
      const [data, complianceRuleset, citiesNearby] = await Promise.all([
        getProductsV3(apiParams),
        getRegionComplianceRules(state_code, countryCode),
        getShopCitiesNearMe({
          countryCode: countryCode,
          location: userLocation,
          retailType: "dispensary",
        }),
      ]);

      const allowedEnvironments = ["staging", "integration", "local-dev"];
      const includeDebugInformation = allowedEnvironments.includes(
        serverConfig.env,
      );

      return {
        props: {
          availableFilters: data?.metadata.availableFilters || [],
          citiesNearby: citiesNearby,
          complianceRuleset: complianceRuleset,
          debugAPIQueryString: includeDebugInformation
            ? leaflyShopStringify(apiParams)
            : undefined,
          disableFixedHeader: true,
          shopItems: data?.data || [],
          totalCount: data?.metadata.totalCount || 0,
        },
      };
    } catch (error) {
      logError(error.toString(), {
        functionName: "/shop getServerSideProps",
      });
      return {
        props: {
          availableFilters: [],
          citiesNearby: [],
          complianceRuleset: null,
          disableFixedHeader: true,
          shopItems: [],
          totalCount: 0,
        },
      };
    }
  },
);

const getLegacyFilterParamsRedirect = (
  context: Parameters<
    ReturnType<typeof createGetServerSideProps<ShopProps>>
  >[0],
): Redirect | undefined => {
  const [url, _hash] = context.resolvedUrl.split("#");
  const [path, query] = url.split("?");
  const { filter, fulfillmentPreference, ...others } = parse(query);

  const shouldUpdateFilters =
    filter &&
    Object.keys(filter).some((appliedFilter) =>
      Object.keys(LEGACY_FILTER_MAP).includes(appliedFilter),
    );
  // * Handle legacy filters & fulfillment param by redirecting update filter keys, if detected.
  if (shouldUpdateFilters || fulfillmentPreference) {
    return {
      destination: `${path}?${leaflyShopStringify({
        filter: normalizeShopFilterParam(filter as Query),
        fulfillment: [fulfillmentPreference],
        ...others,
      })}`,
      statusCode: 302,
    };
  }
};

export default ShopPage;
